import { msg } from "@lingui/core/macro";
import { createSignal, onCleanup, onMount, Show } from "solid-js";
import { Check, Cloud, Loader2 } from "lucide-solid";
import { Card, CardContent } from "@/components/ui/card";
import { useI18n } from "@/providers/i18n-provider";
import type { CatalogFont } from "@/utils/font-downloader";

interface FontCardProps {
  font: CatalogFont;
  isSelected: boolean;
  isDownloading: boolean;
  onSelect: (font: CatalogFont) => void;
  /** Called once when the card scrolls into view, to lazily load its preview font. */
  onVisible?: (font: CatalogFont) => void;
}

export function FontCard(props: FontCardProps) {
  const { t } = useI18n();
  const [cardRef, setCardRef] = createSignal<HTMLDivElement>();

  // Lazily load the preview font only when the card enters the viewport so we
  // never fetch 25 fonts up-front. The browser repaints the preview text
  // automatically once the FontFace is registered.
  onMount(() => {
    const el = cardRef();
    if (!el || !props.onVisible) {
      return;
    }
    if (typeof IntersectionObserver === "undefined") {
      props.onVisible(props.font);
      return;
    }
    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) {
            props.onVisible?.(props.font);
            observer.disconnect();
            break;
          }
        }
      },
      { rootMargin: "200px" },
    );
    observer.observe(el);
    onCleanup(() => {
      observer.disconnect();
    });
  });

  const select = () => {
    props.onSelect(props.font);
  };

  return (
    <Card
      ref={setCardRef}
      // oxlint-disable-next-line jsx-a11y/prefer-tag-over-role -- Card is a styled container kept as a div; button semantics are provided via role + keyboard handlers.
      role="button"
      tabindex={0}
      aria-pressed={props.isSelected}
      aria-label={(() => {
        const { name } = props.font;
        return t(msg`Apply font ${name}`);
      })()}
      class={`cursor-pointer overflow-hidden group hover:shadow-lg transition-[border-color,box-shadow,background-color,transform] duration-300 will-change-transform border focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 active:scale-[0.99] ${
        props.isSelected
          ? "border-primary bg-primary/5 shadow-md shadow-primary/5"
          : "border-border/10 bg-card hover:border-primary/40"
      }`}
      onClick={select}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          select();
        }
      }}
    >
      <CardContent class="p-5 space-y-4 flex flex-col justify-between h-full">
        <div class="flex items-start justify-between gap-4">
          <div>
            <h3 class="font-bold text-lg group-hover:text-primary transition-colors flex items-center gap-1.5">
              {props.font.name}
              <Show when={props.isSelected}>
                <span class="p-0.5 rounded-full bg-primary text-primary-foreground">
                  <Check class="h-3 w-3" />
                </span>
              </Show>
            </h3>
            <span class="text-xs uppercase font-bold text-muted-foreground tracking-wider">
              {props.font.category}
            </span>
          </div>

          <div class="flex items-center gap-2">
            <Show when={props.isDownloading}>
              <Loader2 class="h-5 w-5 text-primary animate-spin" />
            </Show>
            <span class="text-xs bg-secondary/30 px-2 py-1 rounded-md text-muted-foreground font-semibold flex items-center gap-1">
              <Cloud class="h-3.5 w-3.5" />
              Cloud
            </span>
          </div>
        </div>

        {/* Live preview */}
        <div
          class="h-24 bg-secondary/10 rounded-xl flex items-center p-4 overflow-hidden border border-border/5"
          style={{ "font-family": `'${props.font.family}', sans-serif` }}
        >
          <div class="space-y-1">
            {/* eslint-disable-next-line lingui/no-unlocalized-strings -- TODO(i18n): font preview text */}
            <p class="text-xl font-bold">Aa Bb Cc 123</p>
            <p class="text-xs text-muted-foreground">
              {t(msg`The quick brown fox jumps over the lazy dog.`)}
            </p>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
